import { ChangeEvent, DragEvent, useState } from 'react';

import { IconUploadMS } from '../../../../constants/icons';
import { Constants } from '../../constants';
import {
  ReportDropArea,
  ReportDropIcon,
  ReportDropLabel,
  ReportDropSubtitle,
  ReportDropTitle,
} from './styles';

interface FileDropBoxProps {
  /** Precisa ser único na tela: é o `htmlFor` que liga o rótulo ao input. */
  inputId: string;
  title: string;
  subtitle: string;
  multiple?: boolean;
  accept?: string;
  onSelect: (files: File[]) => void;
}

/**
 * Caixa tracejada de seleção de arquivo, por clique ou arraste.
 *
 * Cópia local do padrão de `pages/Hive/Tabs/Notices/components/DocumentUploadBox`
 * — copiado e não importado de propósito: Hive e PreFlight não têm relação, e
 * importar entre as duas áreas acoplaria uma à outra sem motivo.
 *
 * É SÓ APRESENTAÇÃO. Toda validação (PDF, obrigatoriedade) fica em quem usa,
 * para haver um único lugar onde a regra do laudo mora.
 */
export function FileDropBox({
  inputId,
  title,
  subtitle,
  multiple = false,
  accept,
  onSelect,
}: FileDropBoxProps): JSX.Element {
  const [dragActive, setDragActive] = useState(false);

  const handleDrag = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(event.type === 'dragenter' || event.type === 'dragover');
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);
    const files = Array.from(event.dataTransfer.files ?? []);
    if (files.length > 0) onSelect(multiple ? files : files.slice(0, 1));
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    // Alias local porque o lint proíbe escrever em propriedade de parâmetro.
    const input = event.target;
    const files = Array.from(input.files ?? []);
    // Limpa o valor: escolher o MESMO arquivo de novo precisa voltar a
    // disparar `change`, senão a segunda tentativa não faz nada.
    input.value = '';
    if (files.length > 0) onSelect(files);
  };

  return (
    <ReportDropArea
      dragActive={dragActive}
      onDragEnter={handleDrag}
      onDragOver={handleDrag}
      onDragLeave={handleDrag}
      onDrop={handleDrop}
    >
      <ReportDropLabel htmlFor={inputId}>
        <input
          id={inputId}
          type="file"
          accept={accept}
          multiple={multiple}
          style={{ display: 'none' }}
          onChange={handleChange}
        />
        {dragActive ? (
          <ReportDropTitle>{Constants.attachmentsDropActive}</ReportDropTitle>
        ) : (
          <>
            <ReportDropIcon>{IconUploadMS}</ReportDropIcon>
            <ReportDropTitle>{title}</ReportDropTitle>
            <ReportDropSubtitle>{subtitle}</ReportDropSubtitle>
          </>
        )}
      </ReportDropLabel>
    </ReportDropArea>
  );
}
